// Single source of truth for the OS-level (prefers-color-scheme) theme.
//
// Why this lives in Redux rather than a per-component hook: a user whose stored
// preference is 'system' expects an OS theme change to update the WHOLE app —
// including consumers that read the theme from `mapStateToProps` (e.g. a class
// component using the resolved mode as a remount key). A per-component hook only
// updates the components that mount it, leaving Redux/class consumers stale —
// which showed up as "remnants" of the previous theme on OS toggle. Pushing the
// OS mode into Redux makes an OS change propagate exactly like a manual toggle.

export const SET_SYSTEM_THEME_MODE = 'SET_SYSTEM_THEME_MODE'

export const setSystemThemeMode = (mode) => ({ type: SET_SYSTEM_THEME_MODE, payload: mode })

const DARK_QUERY = '(prefers-color-scheme: dark)'

const hasMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'

// Current OS preference, or 'light' when matchMedia is unavailable (SSR/jsdom/old browsers).
export const resolveSystemMode = () => {
  if (!hasMatchMedia()) return 'light'
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

// Subscribe to OS theme changes. Returns an unsubscribe fn. No-op when matchMedia
// is unavailable.
export const subscribeToSystemTheme = (onChange) => {
  if (!hasMatchMedia()) return () => {}

  const mql = window.matchMedia(DARK_QUERY)
  const handler = (event) => onChange(event.matches ? 'dark' : 'light')

  // addEventListener is the modern API; addListener is the Safari < 14 fallback.
  if (mql.addEventListener) {
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }
  mql.addListener(handler)
  return () => mql.removeListener(handler)
}
